var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { SoundManager } from '@shared/utils/SoundManager';
import { AlertUtils } from '@shared/utils/Alert/AlertUtils';
import { AlertAction, AlertActions, AlertEvent, AlertStatus, AlertNotification, AlertCondition, AlertImportance, AlertAfterExecute } from '@shared/utils/Alert/AlertConstants';
import { Alert, AlertSendToServerItem } from './Alert';
import { Resources } from '@shared/localizations/Resources';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Connection } from '../Connection';
import { Message } from '@shared/utils/DirectMessages/DirectMessagesImport';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { Event } from './Event/Event';
import { EventType } from './Event/EventConstants';
import { EventCache } from './Event/EventCache';
import { TIF } from '@shared/utils/Trading/OrderTif';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { messageBoxHandler, alertMessageScreenHandler } from '@shared/utils/AppHandlers';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { ConfirmationTypesEnum } from '@shared/utils/Trading/ConfirmationTypesEnum';
import { SlTpHolder } from '@shared/utils/Trading/SlTpHolder';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { Instrument } from './Instrument';
import { Account } from './Account';
import { BaseSettings } from '../Settings/BaseGeneralSettingsWrapper';
import { BaseSettingsUtils } from '../UtilsClasses/BaseGeneralSettingsUtilsWrapper';
export class AlertManager {
    constructor(dataCache) {
        this.AlertsArray = [];
        this.AlertsDict = {};
        this.ActiveAlertsDict = {}; // instrument tradebleid -> routeid -> alertsArray
        this.OnAlertAdded = new CustomEvent();
        this.OnAlertRemoved = new CustomEvent();
        this.OnAlertUpdated = new CustomEvent();
        this.OnAlertExecuted = new CustomEvent();
        this.AlertsVisible = true;
        this.deferMsg = [];
        this.DataCache = dataCache;
        AlertUtils.DataCache = dataCache;
    }
    // 100929
    // AlertManager.MAX_ALERT = 5; //id:99983, id:99971 пункт 2
    GetFilteredAlerts(instrument, account) {
        if (!this.AlertsVisible) {
            return [];
        }
        return this.GetAllAlerts().filter((alert) => Boolean(alert.Active) &&
            Account.IsEqualAccount(account, alert.Account) &&
            Instrument.IsEqualInstrument(instrument, alert.Instrument));
    }
    Clear() {
        this.AlertsArray.forEach(function (alert) {
            alert.dispose();
        }); // прежде чем удалить ссылки на алерты нужно отписаться от котировок, на которые подписался OrderEdit когда происходило создание alert.OrderPlaceParametrs
        this.AlertsArray = [];
        this.AlertsDict = {};
        this.ActiveAlertsDict = {};
    }
    GetAllAlerts() {
        return this.AlertsArray;
    }
    AddAlert(alert) {
        this.AlertsArray.push(alert);
        this.AlertsDict[alert.AlertId] = alert;
        this.OnAlertAdded.Raise(alert);
    }
    MakeAlertAction(alert, alertCommand) {
        const action = { AlertId: alert.AlertId, Command: alertCommand, Account: alert.Account };
        Connection.vendor.MakeAlertAction(action);
    }
    ChartNewAlert(param) {
        var _a;
        if (!param) {
            return;
        }
        const instrument = param.instrument;
        if (!instrument) {
            return;
        }
        const Level1 = instrument.Level1;
        if (!Level1) {
            return;
        }
        const alertType = (_a = AlertUtils.GetAlertType(param.historyType)) !== null && _a !== void 0 ? _a : AlertUtils.GetDefaultAlertTypeByInstrument(instrument);
        const valueFromInstrument = AlertUtils.GetCorrectPriceForAlertByInstrument(instrument, param.historyType);
        const condition = param.value > valueFromInstrument ? AlertCondition.MORE_EQUAL : AlertCondition.LESS_EQUAL;
        const messageText = AlertUtils.GetMessageText(instrument, alertType, condition, instrument.formatPrice(param.value), param.Account);
        const DataTime = new Date();
        DataTime.setHours(23);
        DataTime.setMinutes(59);
        DataTime.setSeconds(0);
        DataTime.setDate(DataTime.getDate() + 30);
        const dataToSend = new AlertSendToServerItem();
        dataToSend.Account = param.Account;
        dataToSend.Instrument = instrument;
        dataToSend.AlertType = alertType;
        dataToSend.Condition = condition;
        dataToSend.Value = param.value;
        dataToSend.Importance = param.Importance;
        dataToSend.AfterExecute = param.AfterExecute;
        dataToSend.Action = AlertAction.NOTIFY;
        dataToSend.Notification = param.Notification;
        dataToSend.Expiration = DataTime;
        dataToSend.IsAllowOTHTrading = param.IsAllowOTHTrading;
        dataToSend.MessageText = messageText;
        dataToSend.Active = true;
        dataToSend.leverageValue = param.leverageValue;
        this.SendAlertToServer(dataToSend);
    }
    ReplaceAlertFromChart(alert, data) {
        /* #98678 */
        let condition = data.Condition;
        if (condition === null) {
            condition = alert.Condition;
        }
        alert.MessageText = AlertUtils.GetMessageText(alert.Instrument, alert.AlertType, condition, alert.Instrument.formatPrice(data.Value), alert.Account);
        const dataToSend = alert.GetDataToSend();
        dataToSend.Value = data.Value;
        dataToSend.Condition = condition;
        this.SendAlertToServer(dataToSend);
    }
    RemoveAlert(alert) {
        const index = this.AlertsArray.indexOf(alert);
        this.AlertsArray.splice(index, 1);
        this.AlertsDict[alert.AlertId] = null;
        this.OnAlertRemoved.Raise(alert);
    }
    RemoveAlertProcess(alert, afterExecutingCB = null, confirm = true) {
        afterExecutingCB = afterExecutingCB || null;
        const doEvt = function () {
            this.MakeAlertAction(alert, AlertActions.DELETE);
            if (afterExecutingCB) {
                afterExecutingCB();
            }
        }.bind(this);
        const OkCb = function (showNextTime) {
            BaseSettingsUtils.updateConfirmation(confirmationType, showNextTime);
            doEvt();
        };
        const confirmationType = ConfirmationTypesEnum.AlertCancel;
        if (confirm && BaseSettingsUtils.useConfirmation(confirmationType)) {
            messageBoxHandler.Show(Resources.getResource('screen.Alerts.Alert'), Resources.getResource('screen.Alerts.RemoveAlertMessage'), messageBoxHandler.msgType.Question, OkCb, afterExecutingCB, true, false, '');
        }
        else {
            doEvt();
        }
    }
    NewMessage(message) {
        if (!this.DataCache.Loaded) {
            this.deferMsg.push(message);
            return;
        }
        switch (message.Code) {
            case Message.CODE_ALERT_MESSAGE:
            case Message.CODE_REPLACE_ALERT_MESSAGE:
                void this.ProcessNewOrReplaceAlertMessage(message);
                break;
            case Message.CODE_CONTROL_ALERT_COMMAND_MESSAGE:
                this.ProcessControlComandMessage(message);
                break;
        }
    }
    ProcessDeferredMessages() {
        for (let i = 0; i < this.deferMsg.length; i++) {
            this.NewMessage(this.deferMsg[i]);
        }
        this.deferMsg.length = 0;
    }
    ProcessNewOrReplaceAlertMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const instrument = yield this.DataCache.getInstrumentByInstrumentTradableID_NFL(message.InstrumentTradableID, message.RouteId);
            if (isNullOrUndefined(instrument)) {
                return;
            }
            const myAlert = this.GetAlert(message.AlertId);
            const paramObj = {
                Instrument: instrument,
                Account: message.AccountId != null
                    ? this.DataCache.GetAccountById(message.AccountId.toString())
                    : this.DataCache.getPrimaryAccount(),
                AlertId: message.AlertId,
                AlertType: message.AlertType,
                Condition: message.AlertCondition,
                Value: message.AlertValue,
                Importance: message.AlertImportance,
                Expiration: message.Date,
                AfterExecute: message.AlertAfterExecute,
                Action: message.AlertAction,
                Notification: message.AlertNotification,
                IsAllowOTHTrading: message.IsAllowOTHTrading,
                MessageText: message.Text,
                Event: message.AlertEvent,
                IsActive: message.AlertStatus === AlertStatus.ACTIVE
            };
            this.CreateAlertEvent(message.AlertId, message.AlertEvent, message.Text);
            if (message.AlertEvent === AlertEvent.EXECUTED) {
                let messageText = message.Text;
                if (message.AlertAction === AlertAction.PLACE_ORDER_NOTIFY) {
                    let orderText = yield myAlert.OrderPlaceParametrs.getConfirmationText();
                    orderText = '&lt' + orderText.replace(' ?', '') + '&gt';
                    messageText += '; ' + Resources.getResource('screen.Alerts.SendOeAlert').replace('{0}', orderText);
                }
                this.AlertExecuted(message.AlertNotification, messageText, message.AlertImportance);
            }
            if (myAlert) {
                if (message.AlertEvent === AlertEvent.DELETED) {
                    this.RemoveAlert(myAlert);
                }
                else {
                    if (myAlert.Active && !paramObj.IsActive) {
                        const EventText = Resources.getResource('alert.event.text.stopped')
                            .replace('{0}', paramObj.AlertId.toString())
                            .replace('{1}', paramObj.MessageText);
                        const ActionText = 'alert.event.header.stopped';
                        const ev = new Event({
                            DateTime: DateTimeUtils.DateTimeUtcNow(),
                            Action: ActionText,
                            Description: EventText,
                            EventType: EventType.System
                        });
                        EventCache.Add(ev);
                    }
                    this.UpDateAlert(myAlert, paramObj);
                    if (message.OrderPlaceParametrs) {
                        const me = this;
                        this.OrderPlaceParametrsMsgRestore(message.OrderPlaceParametrs).then(function (params) {
                            myAlert.OrderPlaceParametrs = params;
                            me.OnAlertUpdated.Raise(myAlert);
                        });
                    }
                }
            }
            else {
                const alert = new Alert(this.DataCache, paramObj);
                this.AddAlert(alert);
                if (message.OrderPlaceParametrs) {
                    const me = this;
                    this.OrderPlaceParametrsMsgRestore(message.OrderPlaceParametrs).then(function (params) {
                        alert.OrderPlaceParametrs = params;
                        me.OnAlertUpdated.Raise(alert);
                    });
                }
            }
        });
    }
    CreateAlertEvent(AlertId, alertEvent, MessageText) {
        let EventText = '';
        let ActionText = '';
        if (alertEvent === null) {
            return;
        }
        switch (alertEvent) {
            case AlertEvent.DELETED:
                EventText = Resources.getResource('alert.event.text.deleted')
                    .replace('{0}', AlertId)
                    .replace('{1}', MessageText);
                ActionText = 'alert.event.header.deleted';
                break;
            case AlertEvent.EXECUTED:
                EventText = Resources.getResource('alert.event.text.executed')
                    .replace('{0}', AlertId)
                    .replace('{1}', MessageText);
                ActionText = 'alert.event.header.executed';
                break;
            case AlertEvent.EXPIRED:
                EventText = Resources.getResource('alert.event.text.expired')
                    .replace('{0}', AlertId)
                    .replace('{1}', MessageText);
                ActionText = 'alert.event.header.expired';
                break;
            case AlertEvent.CREATED:
                EventText = Resources.getResource('alert.event.text.created')
                    .replace('{0}', AlertId)
                    .replace('{1}', MessageText);
                ActionText = 'alert.event.header.created';
                break;
            case AlertEvent.MODIFIED:
                EventText = Resources.getResource('alert.event.text.modified')
                    .replace('{0}', AlertId)
                    .replace('{1}', MessageText);
                ActionText = 'alert.event.header.modified';
                break;
            case AlertEvent.RESTARTED:
                EventText = Resources.getResource('alert.event.text.restarted')
                    .replace('{0}', AlertId)
                    .replace('{1}', MessageText);
                ActionText = 'alert.event.header.restarted';
                break;
        }
        const ev = new Event({
            DateTime: DateTimeUtils.DateTimeUtcNow(),
            Action: ActionText,
            Description: EventText,
            EventType: EventType.System
        });
        EventCache.Add(ev);
    }
    ProcessControlComandMessage(message) {
        const myAlert = this.GetAlert(message.AlertId);
        if (message.AlertCommand === AlertAfterExecute.STOP) {
            this.UpDateAlert(myAlert, { Active: false });
        }
        if (message.AlertCommand === AlertAfterExecute.REMOVE) {
            this.RemoveAlert(myAlert);
        }
    }
    GetAlert(alertId) {
        return this.AlertsDict[alertId] || null;
    }
    UpDateAlert(alert, data) {
        if (!alert || !data) {
            return;
        }
        if (data.AlertType !== null && data.AlertType !== undefined) {
            alert.AlertType = data.AlertType;
        }
        if (data.Condition !== null && data.Condition !== undefined) {
            alert.Condition = data.Condition;
        }
        if (data.Notification !== null && data.Notification !== undefined) {
            alert.Notification = data.Notification;
        }
        if (data.Expiration !== null && data.Expiration !== undefined) {
            alert.Expiration = data.Expiration;
        }
        if (data.Value !== null && data.Value !== undefined) {
            alert.Value = data.Value;
        }
        if (data.Importance !== null && data.Importance !== undefined) {
            alert.Importance = data.Importance;
        }
        if (data.AfterExecute !== null && data.AfterExecute !== undefined) {
            alert.AfterExecute = data.AfterExecute;
        }
        if (data.Account !== null && data.Account !== undefined) {
            alert.Account = data.Account;
        }
        if (data.Action !== null && data.Action !== undefined) {
            alert.Action = data.Action;
        }
        alert.IsAllowOTHTrading = data.IsAllowOTHTrading;
        if (data.MessageText !== null && data.MessageText !== undefined) {
            alert.MessageText = data.MessageText;
        }
        if (data.OrderPlaceParametrs) {
            alert.OrderPlaceParametrs = data.OrderPlaceParametrs;
        }
        if (data.Instrument !== null && data.Instrument !== undefined) {
            alert.Instrument = data.Instrument;
        }
        if (data.IsActive !== null && data.IsActive !== undefined) {
            alert.Active = data.IsActive;
        }
        this.OnAlertUpdated.Raise(alert);
    }
    // migration boolshit
    createAlertFromSaveItem(alertSaveItem) {
        if (alertSaveItem.Condition === 4 || alertSaveItem.Condition === 5) {
            // EQUAL || NOT_EQUAL
            return null;
        }
        if (!alertSaveItem.MessageText) {
            return null;
        }
        switch (alertSaveItem.Importance) {
            case 0: // HIGH
                alertSaveItem.Importance = AlertImportance.HIGH;
                break;
            case 1: // MEDIUM
                alertSaveItem.Importance = AlertImportance.MEDIUM;
                break;
            case 2: // LOW
                alertSaveItem.Importance = AlertImportance.LOW;
                break;
        }
        switch (alertSaveItem.Action) {
            case 3: // PLACE_ORDER: 3
                alertSaveItem.Action = AlertAction.PLACE_ORDER_NOTIFY;
                break;
            default:
                alertSaveItem.Action = AlertAction.NOTIFY;
        }
        const DataTime = new Date();
        DataTime.setSeconds(0);
        DataTime.setDate(DataTime.getDate() + 30);
        alertSaveItem.Notification = AlertNotification.SHOW_POP_UP | AlertNotification.SOUND;
        alertSaveItem.Expiration = DataTime;
        const dataToSend = new AlertSendToServerItem();
        dataToSend.Instrument = this.DataCache.getInstrumentByName(alertSaveItem.Instrument);
        dataToSend.Account = alertSaveItem.Account;
        dataToSend.AlertType = alertSaveItem.AlertType;
        dataToSend.Condition = alertSaveItem.Condition;
        dataToSend.Value = alertSaveItem.Value;
        dataToSend.Importance = alertSaveItem.Importance;
        dataToSend.AfterExecute = alertSaveItem.AfterExecute;
        dataToSend.Action = alertSaveItem.Action;
        dataToSend.Notification = alertSaveItem.Notification;
        dataToSend.Expiration = alertSaveItem.Expiration;
        dataToSend.IsAllowOTHTrading = alertSaveItem.IsAllowOTHTrading;
        dataToSend.MessageText = alertSaveItem.MessageText;
        return dataToSend;
    }
    AddAlertMigration(alertData) {
        // TMP TODO
        // this.AddAlert(alertData);
        this.SendAlertToServer(alertData);
    }
    OrderPlaceParametrsMsgRestore(OrderPlaceParametrs) {
        if (!isValidArray(OrderPlaceParametrs)) {
            return Promise.resolve(null);
        }
        const firstOrder = OrderPlaceParametrs.at(0);
        const secondOrder = OrderPlaceParametrs.at(1);
        const orderType = !isNullOrUndefined(secondOrder) ? OrderType.OCO : firstOrder.OrderType;
        const dc = this.DataCache;
        const instrumentRequest = dc.getInstrumentByInstrumentTradableID_NFL(firstOrder.tradableInstrumentId, firstOrder.routeId);
        return instrumentRequest.then((instrument) => {
            var _a, _b;
            firstOrder.account =
                (_b = dc.GetAccountByIdOrName((_a = firstOrder.accountId) === null || _a === void 0 ? void 0 : _a.toString())) !== null && _b !== void 0 ? _b : this.DataCache.getPrimaryAccount();
            firstOrder.instrument = instrument;
            // let inLots = SessionSettings.displayAmountInLots();
            // Всегда с сервера в лотах
            firstOrder.quantity = new Quantity(firstOrder.quantity, true);
            const myEdit = dc.OrderParameterContainer.OrderTypes[orderType].createOrderEditObject({
                dataCache: dc
            });
            const ed = new OrderEditUpdateData(null, firstOrder);
            myEdit.placedFrom = PlacedFrom.WEB_ALERT;
            myEdit.updateParameters(ed);
            myEdit.needSetDefaultStopPrice = false;
            myEdit.needSetDefaultLimitPrice = false;
            myEdit.needSetDefaultPrice = false;
            myEdit.isCashQtyMode = firstOrder.cash != null;
            const holder = new SlTpHolder();
            myEdit.sltp.needSetDefaultSLTP = false;
            holder.StopLossLimitPriceValue = firstOrder.slLimitPrice;
            if (firstOrder.slType !== null) {
                holder.StopLossPriceType = firstOrder.slType;
                holder.StopLossPriceValue = firstOrder.slPrice || NaN;
                myEdit.skipSessionSettingsChanged = true;
                if (BaseSettings.isSlTpInOffset || SlTpPriceType.Absolute !== firstOrder.slType) {
                    myEdit.sltp.sl.offsetType = BaseSettings.offsetType;
                    myEdit.sltp.tp.offsetType = BaseSettings.offsetType;
                }
            }
            if (firstOrder.tpType !== null) {
                holder.TakeProfitPriceType = firstOrder.tpType;
                holder.TakeProfitPriceValue = firstOrder.tpPrice || NaN;
                myEdit.skipSessionSettingsChanged = true;
                if (BaseSettings.isSlTpInOffset || SlTpPriceType.Absolute !== firstOrder.tpType) {
                    myEdit.sltp.sl.offsetType = BaseSettings.offsetType;
                    myEdit.sltp.tp.offsetType = BaseSettings.offsetType;
                }
            }
            const iSlTpEditParams = {
                sltpHolder: holder
            };
            myEdit.setSLTP(iSlTpEditParams);
            if (OrderType.Stop === orderType) {
                myEdit.stopPrice = firstOrder.limitPrice;
            }
            else if (OrderType.TrailingStop === orderType) {
                myEdit.trailingStop.value = SlTpUtils.toVisualValue(firstOrder.trailingStopOffset, instrument, myEdit.trailingStop.offsetType);
            }
            else if (OrderType.OCO === orderType) {
                if (firstOrder.OrderType === OrderType.Limit) {
                    myEdit.limitPrice = firstOrder.limitPrice;
                    myEdit.stopPrice = secondOrder.limitPrice;
                }
                else {
                    myEdit.limitPrice = secondOrder.limitPrice;
                    myEdit.stopPrice = firstOrder.limitPrice;
                }
            }
            else {
                myEdit.limitPrice = firstOrder.limitPrice;
                myEdit.stopPrice = firstOrder.stopPrice;
            }
            if (orderType === OrderType.OCO) {
                myEdit.firstOrderType = firstOrder.OrderType;
                myEdit.secondOrderType = secondOrder.OrderType;
                myEdit.firstOrderSide = firstOrder.side;
                myEdit.secondOrderSide = secondOrder.side;
                myEdit.firstOrderTif = new TIF(firstOrder.tif, firstOrder.expire_at);
                myEdit.secondOrderTif = new TIF(secondOrder.tif, secondOrder.expire_at);
            }
            else {
                myEdit.tif = new TIF(firstOrder.tif, firstOrder.expire_at);
            }
            myEdit.isAllowOTHTrading = firstOrder.isAllowOTHTrading;
            myEdit.AlerEdit = true;
            return myEdit;
        });
    }
    AlertExecuted(Notification, MessageText, Importance) {
        if (Notification & AlertNotification.SHOW_POP_UP) {
            if (alertMessageScreenHandler === null || alertMessageScreenHandler === void 0 ? void 0 : alertMessageScreenHandler.Show) {
                alertMessageScreenHandler.Show(Resources.getResource('screen.Alerts.Alert'), MessageText, Importance);
            }
            const executedObj = {
                Title: Resources.getResource('screen.Alerts.Alert'),
                AlertMessageText: MessageText,
                Importance
            };
            this.OnAlertExecuted.Raise(executedObj);
        }
        if (Notification & AlertNotification.SOUND) {
            this.PlaySound(Importance);
        }
    }
    PlaySound(type) {
        let soundKey = null;
        switch (type) {
            case AlertImportance.HIGH:
                soundKey = SoundManager.SoundKeys.AlertHigh;
                break;
            case AlertImportance.MEDIUM:
                soundKey = SoundManager.SoundKeys.AlertMedium;
                break;
            case AlertImportance.LOW:
                soundKey = SoundManager.SoundKeys.AlertLow;
                break;
        }
        SoundManager.tryPlaySound(soundKey);
    }
    GetActiveCount() {
        let counter = 0;
        const alertArray = this.GetAllAlerts();
        for (let i = 0; i < alertArray.length; i++) {
            if (alertArray[i].Active) {
                counter++;
            }
        }
        return counter;
    }
    SendAlertToServer(alertData) {
        Connection.vendor.SendAlertToServer(alertData);
    }
    SortAlertToId(a, b) {
        return b.AlertId - a.AlertId;
    }
}
export const AlertSourceMetodMap = {
    0: 'GetAsk',
    1: 'GetBid',
    2: 'GetLastPrice',
    3: 'GetChangePercent',
    4: 'GetVolume'
};
